// NetworkGuard – full-screen overlay whenever the app loses internet.

import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { PortaledLayer } from '../shared/PortaledLayer';

const CHECK_TIMEOUT_MS = 3000;
const POLL_INTERVAL_MS = 10000;

export function NetworkGuard() {
  const [online, setOnline] = useState(true);

  async function verifyOnline() {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
      await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      setOnline(true);
    } catch {
      setOnline(false);
    }
  }

  useEffect(() => {
    verifyOnline();
    const handleOnline = () => verifyOnline();
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const interval = setInterval(verifyOnline, POLL_INTERVAL_MS);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  if (online) return null;

  return (
    <PortaledLayer>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm dark:bg-black/70">
        <div
          className="mx-4 w-full max-w-sm animate-fade-in overflow-hidden rounded-2xl border border-red-200 bg-white p-8 text-center shadow-xl dark:border-red-500/25 dark:bg-slate-900 dark:text-slate-100"
          role="alert"
        >
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-950/50">
            <WifiOff className="h-7 w-7 text-red-600 dark:text-red-400" aria-hidden />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">No internet connection</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
            Fleet Manager needs a network connection. Reconnect to continue.
          </p>
        </div>
      </div>
    </PortaledLayer>
  );
}
